import { consentAllowsMarketing } from "@/lib/tracking/consent";
import { isTrackingAllowed } from "@/lib/tracking/isTrackingAllowed";

const SCRIPT_ID = "fastsell-fbevents-sdk";

/** Serialized init queue — prevents duplicate fbq('init') when multiple components mount (e.g. strict mode / parent+child). */
let initChain: Promise<void> = Promise.resolve();

let lastInitStoreId: string | null = null;
let lastInitPixelId: string | null = null;

export function isValidMetaPixelId(raw: string | null | undefined): raw is string {
  if (!raw) return false;
  const t = raw.trim();
  return /^[0-9]{15,16}$/.test(t);
}

/** DB / JSONB may expose pixel id as string, integer, or bigint. */
export function normalizeMetaPixelIdFromUnknown(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim().replace(/\s/g, "");
    return isValidMetaPixelId(t) ? t : null;
  }
  if (typeof raw === "number" && Number.isFinite(raw) && Number.isInteger(raw)) {
    const t = String(raw);
    return isValidMetaPixelId(t) ? t : null;
  }
  if (typeof raw === "bigint") {
    const t = raw.toString();
    return isValidMetaPixelId(t) ? t : null;
  }
  return null;
}

export function getMetaPixelInitState(): { storeId: string | null; pixelId: string | null } {
  return { storeId: lastInitStoreId, pixelId: lastInitPixelId };
}

export function resetMetaPixelSingletonForTests(): void {
  lastInitStoreId = null;
  lastInitPixelId = null;
  initChain = Promise.resolve();
}

export function loadFbqScript(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const w = window as Window & { fbq?: (...args: unknown[]) => void };
    w.fbq =
      w.fbq ||
      function push(...args: unknown[]) {
        (w.fbq!.q = w.fbq!.q || []).push(args);
      };
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Meta Pixel script failed to load"));
    document.head.appendChild(s);
  });
}

/**
 * Single-flight init for one (storeId, pixelId). Safe under React Strict Mode and duplicate hooks.
 */
export function ensureMetaPixelInitialized(storeId: string, pixelId: string): Promise<void> {
  if (!isValidMetaPixelId(pixelId)) return Promise.resolve();
  const id = pixelId.trim();
  initChain = initChain
    .then(async () => {
      if (lastInitStoreId === storeId && lastInitPixelId === id) return;
      await loadFbqScript();
      if (typeof window === "undefined") return;
      const w = window as Window & { fbq?: (...args: unknown[]) => void };
      if (typeof w.fbq !== "function") return;
      w.fbq("init", id);
      lastInitStoreId = storeId;
      lastInitPixelId = id;
    })
    .catch(() => {
      /* non-fatal: next init attempt can retry */
    });
  return initChain;
}

/**
 * Map internal / analytics payloads to [Meta Pixel parameter names](https://developers.facebook.com/docs/meta-pixel/reference).
 * Uses `eventID` for Pixel + CAPI deduplication.
 */
export function buildMetaPixelPayload(
  metaEventName: string,
  payload: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  const eid = payload.event_id;
  if (typeof eid === "string" && eid.length > 0) {
    out.eventID = eid;
  }

  const num = (v: unknown): number | undefined => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const strIds = (ids: unknown): string[] | undefined => {
    if (!Array.isArray(ids)) return undefined;
    const s = ids.map((x) => String(x)).filter(Boolean);
    return s.length ? s : undefined;
  };

  switch (metaEventName) {
    case "PageView":
      return Object.keys(out).length ? out : undefined;
    case "ViewContent":
    case "AddToCart": {
      const ids = strIds(payload.content_ids) ?? (payload.product_id != null ? [String(payload.product_id)] : undefined);
      if (ids?.length) out.content_ids = ids;
      out.content_type = typeof payload.content_type === "string" ? payload.content_type : "product";
      const value = num(payload.value);
      if (value !== undefined) out.value = value;
      out.currency = typeof payload.currency === "string" ? payload.currency : "TND";
      return out;
    }
    case "InitiateCheckout": {
      const ids = strIds(payload.content_ids);
      if (ids?.length) out.content_ids = ids;
      const value = num(payload.value);
      if (value !== undefined) out.value = value;
      out.currency = typeof payload.currency === "string" ? payload.currency : "TND";
      const n = num(payload.num_items);
      if (n !== undefined) out.num_items = Math.round(n);
      return Object.keys(out).length ? out : undefined;
    }
    case "Purchase": {
      const value = num(payload.value ?? payload.total);
      if (value !== undefined) out.value = value;
      out.currency = typeof payload.currency === "string" ? payload.currency : "TND";
      const ids = strIds(payload.content_ids);
      if (ids?.length) out.content_ids = ids;
      const n = num(payload.num_items);
      if (n !== undefined) out.num_items = Math.round(n);
      return Object.keys(out).length ? out : undefined;
    }
    case "Lead": {
      const value = num(payload.value);
      out.value = value !== undefined ? value : 0;
      out.currency = typeof payload.currency === "string" ? payload.currency : "TND";
      return Object.keys(out).length ? out : undefined;
    }
    default:
      return Object.keys(out).length ? out : undefined;
  }
}

export function trackMetaStandardEvent(
  metaEventName: string,
  payload?: Record<string, unknown>,
): void {
  if (!isTrackingAllowed() || !consentAllowsMarketing()) return;
  const w = window as Window & { fbq?: (...args: unknown[]) => void };
  if (typeof w.fbq !== "function") return;
  const built =
    payload && Object.keys(payload).length > 0 ? buildMetaPixelPayload(metaEventName, payload) : undefined;
  if (built && Object.keys(built).length > 0) {
    w.fbq("track", metaEventName, built);
  } else {
    w.fbq("track", metaEventName);
  }
}
